/**
 * Pure serialization helpers for custom templates.
 *
 * Two halves of the same round-trip:
 *   - `snapshotSystem` extracts the template-relevant config from a
 *     currently-open HydraulicSystem
 *   - `buildSystemFromCustomTemplate` re-hydrates a saved template into
 *     a new (empty) system suitable for passing to `useCreateModel`
 *
 * Kept here (frontend/utils) rather than in shared/ because the shape
 * it produces is intentionally a *partial* HydraulicSystem — the backend
 * accepts whatever the frontend sends, so we don't try to make it
 * round-trip through the full Zod schema. See the test file for the
 * round-trip invariant we actually enforce.
 *
 * @module template-snapshot
 */
import type { HydraulicSystem } from '@shared/hydraulic'
import {
  CURRENT_TEMPLATE_PAYLOAD_VERSION,
  CURRENT_TEMPLATE_SCHEMA_VERSION,
  type CustomTemplate,
  type TemplatePayload,
} from '@shared/custom-templates/types'

/**
 * Extract the template-relevant slice from a loaded system.
 *
 * Deliberately excludes vertices/edges, document_header, calculation
 * results, and anything else that belongs to a specific model instance.
 * The result is a `TemplatePayload` ready to be wrapped in a
 * CustomTemplate record.
 *
 * @throws Error if the system has no fluid zones or no pipe specs — a
 *   template with zero of either is semantically invalid and
 *   TemplatePayloadSchema would reject it on load anyway. Catching
 *   here gives the save dialog a clean error to show.
 */
export function snapshotSystem(system: HydraulicSystem): TemplatePayload {
  if (!system.fluid_zones || system.fluid_zones.length === 0) {
    throw new Error('Cannot save template: system has no fluid zones')
  }
  if (!system.pipe_specifications || system.pipe_specifications.length === 0) {
    throw new Error('Cannot save template: system has no pipe specifications')
  }

  return {
    version: CURRENT_TEMPLATE_PAYLOAD_VERSION,
    schema_version: CURRENT_TEMPLATE_SCHEMA_VERSION,
    fluid_zones: system.fluid_zones,
    pipe_specifications: system.pipe_specifications,
    ...(system.design_constraints ? { design_constraints: system.design_constraints } : {}),
    calculation_settings: system.calculation_settings,
  }
}

/**
 * Re-hydrate a saved template into a partial HydraulicSystem suitable
 * for passing to the create-model flow.
 *
 * Returns `unknown` because the shape is an intentionally-incomplete
 * HydraulicSystem: empty vertices/edges, no application_type, and a
 * freshly generated document_header with the template's name. The
 * existing `ProjectSelectorDialog` already accepts `system: unknown`
 * (matching `buildSystemForApplication`'s return type), so this slots
 * in without ceremony.
 *
 * Intentionally omits `application_type` so the editor's header badge
 * doesn't mistake a custom-template-sourced model for a built-in preset.
 */
export function buildSystemFromCustomTemplate(template: CustomTemplate): unknown {
  return {
    schema_version: template.payload.schema_version,
    calculation_settings: template.payload.calculation_settings,
    ...(template.payload.design_constraints
      ? { design_constraints: template.payload.design_constraints }
      : {}),
    fluid_zones: template.payload.fluid_zones,
    pipe_specifications: template.payload.pipe_specifications,
    vertices: [],
    edges: [],
    document_header: {
      authorship: {
        project: template.name,
        creation_date: new Date().toISOString(),
      },
    },
  }
}
